import { BlogPost } from '../types';

const post: BlogPost = {
  id: 'our-ethos',
  slug: 'our-ethos-principles-that-drive-us',
  title: 'Our Ethos: 12 Principles That Drive Us',
  excerpt:
    'Discover the core principles that shape our culture, drive our decisions, and define how we create exponential value for our clients.',
  category: 'Culture',
  date: '1 Mar 2024',
  readTime: '8 mins read',
  imageUrl: '/images/blog/ethos.jpg',
  author: {
    name: 'Zac Brooks',
    imageUrl: '/images/blog/zac-brooks.jpg',
  },
  content: [
    {
      type: 'twoColumnProblemOverview',
      content: {
        problem: {
          title: 'WHY ETHOS MATTERS',
          description:
            'Culture is not just what we say—it is how we operate every day. Our ethos shapes every decision, interaction, and outcome we create.',
        },
        background: {
          title: 'IMPACT',
          description: 'These principles have helped us:',
          bulletPoints: [
            'Move faster than competitors',
            'Build stronger client relationships',
            'Create exponential value',
            'Maintain excellence at scale',
          ],
        },
      },
    },

    {
      type: 'markdown',
      content: `
The current of business pushes us toward working harder - more hours, more meetings, more manual effort. But that current leads to burnout, not breakthrough. These principles are deliberately counterintuitive because exponential results require a fundamentally different approach.

In the era of AI and rapid technological change, success comes from working smarter, not harder. Our ethos is designed to resist the gravitational pull of busy work and create space for genuine innovation. These principles guide all three arms of our organization - our Agency that builds transformative solutions, our Ventures that invest in exponential opportunities, and our Institute that shapes the future of work.

Each principle challenges conventional wisdom. They may feel uncomfortable at first because they require us to unlearn traditional approaches to work. Our goals and aspirations are not to be the same as everyone else.`,
    },

    {
      type: 'keyBulletList',
      content: {
        heading: '1. The ultimate OKR is iteration speed',
        description:
          'How quickly we create, test, adapt, and make decisions drives our success. At every level, rapid iteration is paramount.',
        points: [
          {
            icon: 'lightbulb',
            title: 'Why It Matters',
            text: 'The natural pull is to perfect in isolation. But we are not our users, we are not our customers - their reality is the only truth that matters. Speed of learning is our competitive advantage.',
          },
          {
            icon: 'speed',
            title: 'What It Looks Like Applied',
            text: 'Ship to learn, not learn to ship. Rapid prototypes, constant user feedback, and swift pivots when needed. Everything we do is measured by speed to insight.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '2. We are paid to play',
        description:
          'We are here to have fun. This is all a game, and how we accrue points should be completely transparent.',
        points: [
          {
            icon: 'psychology',
            title: 'Why It Matters',
            text: 'When passion aligns with impact, magic happens. If it feels like a grind, we are doing it wrong. This is a game we get to play - with real rewards, clear achievements, and constant leveling up.',
          },
          {
            icon: 'sports_esports',
            title: 'What It Looks Like Applied',
            text: 'Gamified objectives with clear rewards, visible progress tracking, celebration of wins big and small. The moment it stops being fun, we stop and reassess.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '3. Protect focus relentlessly',
        description:
          'Asynchronous-first, distraction-free, and focus-driven work powers our results.',
        points: [
          {
            icon: 'visibility',
            title: 'Why It Matters',
            text: 'Deep work is not just another productivity hack - it is the fundamental unit of value creation in the knowledge economy. Focus is the bridge between potential and breakthrough.',
          },
          {
            icon: 'do_not_disturb',
            title: 'What It Looks Like Applied',
            text: 'Four-hour focus blocks, zero-notification environments, and clear boundaries between deep and shallow work. We measure our day by focus time, not busy time.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '4. Our people are the product',
        description:
          'Empowering others is the most powerful investment we make. Give more than you take. Invest in generosity—our success is built on contributions, not just transactions.',
        points: [
          {
            icon: 'diamond',
            title: 'Why It Matters',
            text: 'Every day we face a choice: optimize for short-term output or long-term capability. Investing in our people multiplies every other investment we make. Products are our output, but people are our multiplier.',
          },
          {
            icon: 'groups',
            title: 'What It Looks Like Applied',
            text: 'Dedicated growth budgets, protected learning time, and clear progression paths. Success is measured by the growth of our people, not just the growth of our products.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '5. Empathy multiplies impact',
        description:
          'Always operate with intense empathy—it will amplify everything you create.',
        points: [
          {
            icon: 'psychology',
            title: 'Why It Matters',
            text: 'True innovation starts with feeling what others feel. When we deeply understand our users, customers, and teammates, we solve problems they have not even articulated yet. Assuming best intent transforms every interaction.',
          },
          {
            icon: 'favorite',
            title: 'What It Looks Like Applied',
            text: 'Regular user shadowing, empathy interviews with customers, and team retrospectives that dig deep into experiences, not just outcomes. We celebrate those who go above and beyond for others.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '6. Everything is for an outcome',
        description:
          'Every action moves toward a measurable goal. We may not plan the whole journey upfront, but we always have a north star and a clear purpose.',
        points: [
          {
            icon: 'gps_fixed',
            title: 'Why It Matters',
            text: 'Motion is not progress. Every line of code, every meeting, every decision must drive toward measurable impact. We are evidence-driven, measuring what matters and ignoring vanity metrics.',
          },
          {
            icon: 'target',
            title: 'What It Looks Like Applied',
            text: 'Clear success metrics before starting, regular impact reviews, and ruthless prioritization based on real-world outcomes. If we cannot measure it, we rethink it.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '7. Grow or die',
        description:
          'Stagnation is the enemy; continuous growth is the path forward. Small, consistent improvements lead to exponential progress.',
        points: [
          {
            icon: 'rocket',
            title: 'Why It Matters',
            text: 'In a world of exponential change, staying still means falling behind. Growth is not linear - it is a skill tree where each new capability unlocks new possibilities.',
          },
          {
            icon: 'trending_up',
            title: 'What It Looks Like Applied',
            text: 'Visible skill trees for every role, active mentorship programs, and clear leveling systems. Each project is a quest for new abilities, not just deliverables.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '8. Applied over concepts',
        description:
          'We do not learn first, we try first, then learn. We believe the best way to learn is to skydive into the jungle with a spirit guide.',
        points: [
          {
            icon: 'science',
            title: 'Why It Matters',
            text: 'Traditional learning starts broad and theoretical. We start narrow and practical - letting real needs guide what we learn. This focuses our learning on the pieces that matter most right now.',
          },
          {
            icon: 'engineering',
            title: 'What It Looks Like Applied',
            text: 'Learning through real projects, guided by experienced mentors. We build first, then understand deeper. Each project teaches us exactly what we need to learn next.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '9. We are asynchronous-first',
        description:
          'If it cannot be done asynchronously, something is broken. Meetings should be reserved for one-on-one investment.',
        points: [
          {
            icon: 'watch_later',
            title: 'Why It Matters',
            text: 'Meetings are for energy transfer - collaboration, mentorship, and breakthroughs. Everything else should be async. Clear writing and documentation scale infinitely.',
          },
          {
            icon: 'schedule',
            title: 'What It Looks Like Applied',
            text: 'Rich documentation, clear written updates, and meetings only for high-energy collaboration. We optimize for global, timezone-independent progress.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '10. Our habits and perspectives define us',
        description:
          'The "how" is more important than the "what". We are mindful of our habits, processes, and believe that blended perspectives is how we achieve what others cannot.',
        points: [
          {
            icon: 'psychology',
            title: 'Why It Matters',
            text: 'Excellence is not an act, but a habit. The systems and perspectives we cultivate daily shape not just what we achieve, but who we become. Diversity of thought is our superpower.',
          },
          {
            icon: 'settings',
            title: 'What It Looks Like Applied',
            text: 'Daily habit tracking, cross-pollination of ideas between teams, and systematic reflection on our processes. We actively seek out different viewpoints and ways of thinking.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '11. Build the plane while flying',
        description:
          'Refactor, automate, and fix processes - not before, but during.',
        points: [
          {
            icon: 'construction',
            title: 'Why It Matters',
            text: 'Perfect systems do not exist - only improving ones. The best time to improve is in the heat of real work, where constraints and opportunities are clearest.',
          },
          {
            icon: 'flight_takeoff',
            title: 'What It Looks Like Applied',
            text: 'Constant process evolution, real-time system improvements, and treating every project as a chance to level up our infrastructure. We fix things as we find them.',
          },
        ],
      },
    },
    {
      type: 'keyBulletList',
      content: {
        heading: '12. Automate everything',
        description: 'If the result can still be the same, automate it now.',
        points: [
          {
            icon: 'precision_manufacturing',
            title: 'Why It Matters',
            text: 'Every manual task is a tax on our potential. Automation is not about replacing humans - it is about elevating human work to its highest level of creativity and impact.',
          },
          {
            icon: 'smart_toy',
            title: 'What It Looks Like Applied',
            text: 'Automation-first mindset in every role, building tools that build tools, and creating systems that get smarter over time. No one should do the same task twice.',
          },
        ],
      },
    },

    {
      type: 'styledCallout',
      content: {
        text: 'Culture is not what we say - it is what we do when no one is looking.',
        accentColor: 'border-brand-purple dark:border-brand-purple',
      },
    },

    {
      type: 'impactStatement',
      content:
        'These principles are not just words on a wall - they are how we operate every day.',
    },

    {
      type: 'comparisonTable',
      content: {
        headers: ['Branch', 'How Our Ethos Manifests'],
        rows: [
          [
            'Agency',
            'Transforming client operations through rapid iteration, deep empathy, and intelligent automation. We build solutions that multiply human potential.',
          ],
          [
            'Ventures',
            'Creating products that embody our principles - async-first, automation-driven, and designed for exponential impact. We invest in tools that reshape how work happens.',
          ],
          [
            'Institute',
            'Teaching others to work smarter, not harder. Sharing our learnings about focus, automation, and human-centered tech. We shape the future of knowledge work.',
          ],
        ],
      },
    },
  ],
};

export default post;
